import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, ShoppingCart, BookOpen, MessageCircle, ExternalLink, Instagram, Twitter, Share2, Tag, ChevronRight, Play } from "lucide-react";
import { toast } from "sonner";
import { useSuspenseQuery } from "@tanstack/react-query";
import { fetchNovelBySlug, fetchSettings } from "@/lib/api";
import { lazy, Suspense } from "react";
const CommentSection = lazy(() =>
  import("@/components/comment-section").then((m) => ({ default: m.CommentSection })),
);
import { parseContent, getImageProps, splitSlug } from "@/lib/utils";
import { AdSlot, CustomAdSlot } from "@/components/ad-slot";
import { DonationBox } from "@/components/donation-box";

export const Route = createFileRoute("/novel/$id/$slug/")({
  loader: async ({ params, context: { queryClient } }) => {
    try {
      const fullSlug = params.id === "p" ? params.slug : `${params.slug}-${params.id}`;
      const [novel, settings] = await Promise.all([
        queryClient.ensureQueryData({
          queryKey: ["novel", fullSlug],
          queryFn: () => fetchNovelBySlug(fullSlug),
        }),
        queryClient.ensureQueryData({
          queryKey: ["settings"],
          queryFn: fetchSettings,
        }),
      ]);
      return { novel, settings };
    } catch (e) {
      throw notFound();
    }
  },
  head: ({ loaderData, params }) => {
    const data = loaderData as any;
    const settings = data?.settings;
    const n = data?.novel;
    const siteName = settings?.site_name || "Lihar Aksara";
    if (!n) return { meta: [{ title: `Tidak ditemukan — ${siteName}` }] };

    const title = n.metaTitle || n.title;
    const synopsis = n.postMeta?.synopsis || "";
    const description = n.metaDescription || (synopsis.length > 160 ? synopsis.substring(0, 157) + "..." : synopsis);
    
    const apiBase = import.meta.env.VITE_API_URL || "/api";
    const baseUrl = apiBase.replace("/api", "");
    const imageUrl = n.coverImage
      ? n.coverImage.startsWith("http")
        ? n.coverImage
        : `${baseUrl}${n.coverImage}`
      : "";

    const canonicalUrl = `https://liharaksara.web.id/novel/${params.id}/${params.slug}`;

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Book",
      "name": title,
      "description": description,
      "image": imageUrl,
      "author": {
        "@type": "Person",
        "name": n.author?.name || siteName,
        "url": `https://liharaksara.web.id/penulis/${n.author?.slug}`
      },
      "publisher": {
        "@type": "Organization",
        "name": siteName
      },
      "url": canonicalUrl,
      "workExample": n.chapters?.map((c: any) => {
          const parts = splitSlug(c.slug || "");
          return {
              "@type": "CreativeWork",
              "name": c.title,
              "url": `https://liharaksara.web.id/novel/${params.id}/${params.slug}/chapter/${parts.id}/${parts.slug}`
          };
      })
    };

    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://liharaksara.web.id"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Novel",
          "item": "https://liharaksara.web.id/novel"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": title,
          "item": canonicalUrl
        }
      ]
    };

    return {
      meta: [
        { title: `${title} — ${siteName}` },
        { name: "description", content: description },
        { name: "keywords", content: `novel, ${title}, ${n.author?.name}, sastra` },
        { property: "og:title", content: `${title} — ${siteName}` },
        { property: "og:description", content: description },
        { property: "og:image", content: imageUrl },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "book" },
        { property: "og:site_name", content: siteName },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: imageUrl },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl }
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  const { id, slug } = Route.useParams();
  const fullSlug = id === "p" ? slug : `${slug}-${id}`;
  
  const { data: novel } = useSuspenseQuery({
    queryKey: ["novel", fullSlug],
    queryFn: () => fetchNovelBySlug(fullSlug),
  }) as any;

  const { data: settings } = useSuspenseQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
  }) as any;

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: novel.title, url }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => toast.success("Tautan berhasil disalin!"))
        .catch(() => toast.error("Gagal menyalin tautan."));
    } else {
      toast.error("Fitur salin tidak didukung di browser ini.");
    }
  };

  const whatsappNumber = settings?.whatsapp_number;
  const waMessage = encodeURIComponent(
    `Halo Lihar Aksara, saya ingin memesan novel "${novel.title}". Bagaimana prosedurnya?`,
  );
  const waUrl = whatsappNumber ? `https://wa.me/${whatsappNumber}?text=${waMessage}` : null;

  const marketplacesStr = novel.postMeta?.marketplaces; let marketplaces: any[] = []; try { if (typeof marketplacesStr === "string") { marketplaces = JSON.parse(marketplacesStr); } else if (Array.isArray(marketplacesStr)) { marketplaces = marketplacesStr; } } catch (e) { console.error("Failed to parse marketplaces:", e); }
  const chapters = novel.chapters || [];

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header section matches Article detail */}
      <header className="container-prose pt-12 md:pt-20">
        <Link
          to="/novel"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-accent transition-colors mb-10 group"
        >
          <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />{" "}
          Kembali ke katalog
        </Link>
        
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-4">
          {novel.categories?.[0]?.name || "Novel"}
        </div>
        <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-medium leading-[1.1] tracking-tight text-ink">
          {novel.title}
        </h1>
        
        <div className="mt-8 flex items-center justify-between text-xs sm:text-sm text-muted-foreground border-y border-border py-4">
          <div className="flex items-center gap-3">
            <Link 
              to="/penulis/$slug" 
              params={{ slug: novel.author?.slug || novel.authorId.toString() }}
              className="text-foreground font-bold hover:text-accent transition-colors"
            >
              {novel.author?.name}
            </Link>
            <span>·</span>
            <span className="font-serif italic">Terbitan Tahun {novel.postMeta?.year || new Date(novel.createdAt).getFullYear()}</span>
            {novel.postMeta?.genre && (
              <>
                <span>·</span>
                <span className="font-bold text-accent uppercase tracking-widest text-[9px]">{novel.postMeta.genre}</span>
              </>
            )}
            {novel.postMeta?.novelStatus && (
              <>
                <span>·</span>
                <span className="px-2 py-0.5 bg-secondary text-[9px] font-black uppercase tracking-widest rounded-sm border border-border">{novel.postMeta.novelStatus}</span>
              </>
            )}
          </div>
          <button
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors font-bold uppercase tracking-widest text-[10px]"
            onClick={handleShare}
          >
            <Share2 className="w-3.5 h-3.5" /> Bagikan
          </button>
        </div>
      </header>

      {/* Main image / Cover */}
      <div className="container-prose mt-10">
        <div className="max-w-xs mx-auto md:max-w-sm lg:max-w-md">
          <div className="relative aspect-[2/3] rounded-sm overflow-hidden shadow-2xl bg-muted border border-border/50">
            <img
              {...getImageProps(novel.coverImage)}
              alt={novel.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Action buttons section */}
      <div className="container-prose mt-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
           {chapters.length > 0 && (
              <Link
                 to="/novel/$id/$slug/chapter/$chapterId/$chapterSlug"
                 params={{ 
                   id, 
                   slug, 
                   chapterId: splitSlug(chapters[0].slug || "").id,
                   chapterSlug: splitSlug(chapters[0].slug || "").slug,
                 }}
                 className="w-full py-4 bg-ink text-paper flex items-center justify-center gap-3 font-bold uppercase tracking-widest text-xs rounded-sm shadow-lg hover:bg-accent transition-all"
              >
                 <Play size={16} className="  fill-current" />
                 Mulai Membaca
              </Link>
           )}
           {waUrl && (
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className="w-full py-4 bg-secondary text-foreground border border-border flex items-center justify-center gap-3 font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-border transition-all"
              >
                <ShoppingCart size={16} className=" " />
                Pesan Buku Fisik
              </a>
           )}
        </div>

        {marketplaces.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="text-[10px] uppercase tracking-[0.3em] font-black text-muted-foreground flex items-center gap-4">
              <div className="h-px w-8 bg-border" />
              Beli Online
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {marketplaces.map((m, i) => (
                <a
                  key={i}
                  href={m.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between p-3 bg-card border border-border rounded-sm hover:border-accent/40 hover:bg-accent/[0.02] transition-all group"
                >
                  <span className="text-[10px] font-bold uppercase tracking-widest truncate">{m.name}</span>
                  <ExternalLink size={12} className="  text-muted-foreground group-hover:text-accent" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Synopsis section */}
      <div className="container-prose mt-16">
        <div className="text-[10px] uppercase tracking-[0.4em] font-black text-muted-foreground mb-8">
          Sinopsis Cerita
        </div>
        <div 
          className="ck-content prose prose-slate max-w-none novel-content select-none text-ink/80 text-lg md:text-xl leading-relaxed font-serif" onContextMenu={(e) => e.preventDefault()}
          suppressHydrationWarning dangerouslySetInnerHTML={{ __html: parseContent(novel.postMeta?.synopsis || novel.excerpt || "") }}
        />
      </div>

      {/* Detailed Description section */}
      {novel.html && novel.html !== novel.postMeta?.synopsis && (
        <div className="container-prose mt-16 pt-16 border-t border-border/50">
          <div className="text-[10px] uppercase tracking-[0.4em] font-black text-muted-foreground mb-8">
            Tentang Karya Ini
          </div>
          <div 
            className="ck-content prose prose-slate max-w-none novel-content select-none text-ink/90 text-lg md:text-xl leading-[1.8] font-serif" onContextMenu={(e) => e.preventDefault()}
            suppressHydrationWarning dangerouslySetInnerHTML={{ __html: parseContent(novel.html) }}
          />
        </div>
      )}

      {/* Chapters list section */}
      <div className="container-prose mt-24">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
             <BookOpen size={20} className="  text-accent" />
             <h2 className="font-serif text-2xl md:text-3xl font-medium">Daftar Isi</h2>
          </div>
          <div className="px-4 py-2 bg-secondary rounded-full text-[10px] font-black uppercase tracking-widest text-muted-foreground">
             {chapters.length} Bagian
          </div>
       </div>

       <div className="grid gap-3">
          {chapters.length > 0 ? (
             chapters.map((chap: any) => {
                const chapterParts = splitSlug(chap.slug || "");
                return (
                  <Link
                     key={chap.id}
                     to="/novel/$id/$slug/chapter/$chapterId/$chapterSlug"
                     params={{ 
                       id, 
                       slug,
                       chapterId: chapterParts.id,
                       chapterSlug: chapterParts.slug
                     }}
                     className="group flex items-center justify-between p-4 md:p-6 bg-card border border-border rounded-sm hover:border-accent/40 hover:bg-accent/[0.01] transition-all"
                  >
                     <div className="flex items-center gap-4 md:gap-6">
                        <span className="text-[10px] font-black text-accent/30 group-hover:text-accent transition-colors">
                           {String(chap.order).padStart(2, '0')}
                        </span>
                        <span className="font-serif text-base md:text-lg group-hover:translate-x-1 transition-transform duration-300">
                           {chap.title}
                        </span>
                     </div>
                     <ChevronRight size={16} className="  text-muted-foreground/30 group-hover:text-accent transition-colors" />
                  </Link>
                );
             })
          ) : (
             <div className="py-12 text-center border border-dashed border-border rounded-sm opacity-50 italic text-sm">
                Belum ada bagian yang diterbitkan.
             </div>
          )}
       </div>
      </div>

      {/* Tags section */}
      {novel.tags && novel.tags.length > 0 && (
         <div className="container-prose mt-20 pt-12 border-t border-border flex flex-wrap gap-2">
            {novel.tags.map((tag: any) => (
              <span key={tag.id} className="inline-flex items-center gap-1.5 px-3 py-1 bg-secondary/50 text-[9px] font-black uppercase tracking-widest rounded-sm border border-border text-muted-foreground">
                 <Tag className="w-2.5 h-3" />
                 {tag.name}
              </span>
            ))}
         </div>
      )}

      <div className="container-prose mt-24">
        <DonationBox />
      </div>

      {/* Comment section */}
      <div className="container-prose mt-24">
        <Suspense fallback={<div className="h-32 flex items-center justify-center italic text-muted-foreground">Memuat diskusi...</div>}>
           <CommentSection postId={novel.id} />
        </Suspense>
      </div>
    </div>
  );
}
